/**
 * `blunt` — staff, club, hammer, shield rim, a fall from a crag, a bullock's
 * hoof.
 *
 * The least lethal type and the most immediately disabling: little blood, a
 * great deal of shock, and broken bone from level 3 upward. Blunt wounds heal
 * best of anything in the game and cripple worst if nobody sets them.
 *
 * `break` sits on the type profile rather than on each zone because breaking
 * things is what blunt damage *is*. At the eye that reads as the orbital bone,
 * which is exactly right.
 */
import { ladder } from './authoring.js';
const BLUNT = [
    { bleed: 0, shock: 0, pain: 0, impair: 0, knock: 0, infect: 0 },
    { bleed: 0.5, shock: 1.5, pain: 1, impair: 0.05, knock: 0, infect: 0.01 },
    { bleed: 1, shock: 3, pain: 2, impair: 0.15, knock: -1, infect: 0.02 },
    { bleed: 2, shock: 5, pain: 4, impair: 0.35, knock: -3, infect: 0.04, fx: ['break'] },
    {
        bleed: 4,
        shock: 8,
        pain: 6,
        impair: 0.7,
        knock: -5,
        infect: 0.06,
        fx: ['break', 'knockdown'],
    },
    {
        bleed: 7,
        shock: 11,
        pain: 9,
        impair: 1,
        knock: -7,
        infect: 0.08,
        fx: ['break', 'knockdown'],
    },
];
export const BLUNT_TABLE = {
    head: ladder(BLUNT, {
        shock: 1.4,
        knock: -2,
        fx: [[], [], ['knockdown'], ['knockdown'], ['knockdown'], ['kill']],
    }, [
        [
            '{weapon} raps {victim}’s skull and sets {their} ears ringing.',
            '{weapon} glances off {victim}’s brow.',
        ],
        [
            '{weapon} strikes {victim}’s head and raises a lump.',
            '{weapon} catches {victim} across the cheek and splits the skin.',
        ],
        [
            '{weapon} strikes {victim}’s head and {victim} reels.',
            '{weapon} catches {victim} above the ear and the eyes go glassy.',
        ],
        [
            '{weapon} cracks the bone of {victim}’s skull.',
            '{weapon} breaks {victim}’s jaw and the teeth go with it.',
        ],
        [
            '{weapon} staves in the side of {victim}’s skull.',
            '{weapon} shatters the bone above {victim}’s eye and drives it inward.',
        ],
        ['{weapon} crushes {victim}’s skull.', '{weapon} breaks {victim}’s head open.'],
    ]),
    eye: ladder(BLUNT, { bleed: 0.5, fx: [[], [], [], ['blind'], ['blind'], ['blind']] }, [
        [
            '{weapon} grazes the brow above {victim}’s {part}.',
            '{weapon} strikes near {victim}’s {part} and the eye waters.',
        ],
        [
            '{weapon} strikes {victim}’s {part} and it swells shut.',
            '{weapon} splits the skin over {victim}’s {part}.',
        ],
        [
            '{weapon} strikes {victim}’s {part} and blood fills it.',
            '{weapon} closes {victim}’s {part} with a blow.',
        ],
        [
            '{weapon} breaks the bone around {victim}’s {part}. The eye is ruined.',
            '{weapon} crushes {victim}’s {part} in its socket.',
        ],
        [
            '{weapon} staves in the socket of {victim}’s {part}.',
            '{weapon} shatters the bone around {victim}’s {part} and the eye with it.',
        ],
        [
            '{weapon} drives the bone of {victim}’s socket in upon the {part}.',
            '{weapon} destroys {victim}’s {part} and the socket that held it.',
        ],
    ]),
    neck: ladder(BLUNT, { bleed: 1.5, shock: 1.3, fx: [[], [], [], [], [], ['kill']] }, [
        ['{weapon} glances off {victim}’s neck.', '{weapon} strikes the side of {victim}’s throat.'],
        [
            '{weapon} strikes {victim}’s neck and raises a welt.',
            '{weapon} catches {victim} across the throat and {victim} coughs.',
        ],
        [
            '{weapon} strikes {victim}’s throat and the breath will not come.',
            '{weapon} catches {victim}’s neck and the head snaps aside.',
        ],
        [
            '{weapon} crushes the muscle of {victim}’s neck against the bone.',
            '{weapon} strikes {victim}’s windpipe and {victim} fights for air.',
        ],
        [
            '{weapon} cracks the bone of {victim}’s neck.',
            '{weapon} crushes {victim}’s windpipe and the breath comes in whistles.',
        ],
        ['{weapon} breaks {victim}’s neck.', '{weapon} crushes {victim}’s throat shut.'],
    ]),
    chest: ladder(BLUNT, { shock: 1.2, fx: [[], [], [], [], [], ['kill']] }, [
        ['{weapon} thuds against {victim}’s chest.', '{weapon} glances off {victim}’s ribs.'],
        [
            '{weapon} strikes {victim}’s chest and raises a bruise.',
            '{weapon} catches {victim} across the ribs.',
        ],
        [
            '{weapon} strikes {victim}’s chest and drives the wind out of {them}.',
            '{weapon} slams into {victim}’s ribs and the breath stops.',
        ],
        [
            '{weapon} cracks {victim}’s ribs.',
            '{weapon} breaks a rib in {victim}’s chest and every breath tells {them} so.',
        ],
        [
            '{weapon} staves in {victim}’s ribs.',
            '{weapon} breaks {victim}’s chest inward and the breath comes wet.',
        ],
        [
            '{weapon} crushes {victim}’s chest.',
            '{weapon} drives {victim}’s ribs into the heart behind them.',
        ],
    ]),
    abdomen: ladder(BLUNT, { infect: 2, fx: [[], [], [], [], [], ['kill']] }, [
        ['{weapon} thuds against {victim}’s belly.', '{weapon} glances off {victim}’s hip.'],
        [
            '{weapon} strikes {victim}’s belly and raises a bruise.',
            '{weapon} catches {victim} across the flank.',
        ],
        [
            '{weapon} drives into {victim}’s belly and {victim} doubles over.',
            '{weapon} strikes {victim}’s side and the wind goes out of {them}.',
        ],
        [
            '{weapon} strikes deep into {victim}’s belly and something tears inside.',
            '{weapon} cracks the bone of {victim}’s hip.',
        ],
        [
            '{weapon} ruptures something in {victim}’s belly and the bleeding is all inward.',
            '{weapon} shatters {victim}’s hip and the leg turns wrong.',
        ],
        [
            '{weapon} crushes {victim}’s belly and the bleeding cannot be reached.',
            '{weapon} breaks {victim}’s back at the hip.',
        ],
    ]),
    arm: ladder(BLUNT, {}, [
        ['{weapon} thuds against {victim}’s {part}.', '{weapon} glances off {victim}’s {part}.'],
        [
            '{weapon} strikes {victim}’s {part} and raises a bruise.',
            '{weapon} catches {victim}’s {part} and the arm goes numb.',
        ],
        [
            '{weapon} strikes {victim}’s {part} and the arm drops.',
            '{weapon} slams into the muscle of {victim}’s {part}.',
        ],
        [
            '{weapon} cracks the bone of {victim}’s {part}.',
            '{weapon} breaks {victim}’s {part} above the elbow.',
        ],
        [
            '{weapon} shatters the bone of {victim}’s {part}.',
            '{weapon} breaks {victim}’s {part} in two places and it hangs wrong.',
        ],
        [
            '{weapon} crushes {victim}’s {part} past all use of it.',
            '{weapon} breaks {victim}’s {part} to splinters.',
        ],
    ]),
    hand: ladder(BLUNT, { bleed: 0.6, impair: 1.1 }, [
        ['{weapon} raps {victim}’s {part}.', '{weapon} glances off {victim}’s knuckles.'],
        [
            '{weapon} strikes {victim}’s {part} and the fingers sting.',
            '{weapon} bruises the back of {victim}’s {part}.',
        ],
        [
            '{weapon} strikes {victim}’s {part} and the grip fails.',
            '{weapon} mashes {victim}’s fingers against the haft.',
        ],
        [
            '{weapon} breaks the bones of {victim}’s {part}.',
            '{weapon} snaps two fingers on {victim}’s {part}.',
        ],
        [
            '{weapon} shatters {victim}’s {part}.',
            '{weapon} crushes {victim}’s {part} and the fingers will never straighten.',
        ],
        [
            '{weapon} crushes {victim}’s {part} to pulp.',
            '{weapon} breaks every bone in {victim}’s {part}.',
        ],
    ]),
    leg: ladder(BLUNT, { knock: -2 }, [
        ['{weapon} thuds against {victim}’s {part}.', '{weapon} glances off {victim}’s {part}.'],
        [
            '{weapon} strikes {victim}’s {part} and raises a bruise.',
            '{weapon} catches {victim}’s {part} and the leg goes numb.',
        ],
        [
            '{weapon} strikes {victim}’s {part} and {victim} staggers.',
            '{weapon} slams into the muscle of {victim}’s {part}.',
        ],
        [
            '{weapon} cracks the bone of {victim}’s {part}.',
            '{weapon} breaks {victim}’s knee and the leg folds.',
        ],
        [
            '{weapon} shatters the bone of {victim}’s {part}.',
            '{weapon} breaks {victim}’s {part} and {victim} goes down in a heap.',
        ],
        [
            '{weapon} crushes {victim}’s {part} past all use of it.',
            '{weapon} breaks {victim}’s {part} to splinters.',
        ],
    ]),
    foot: ladder(BLUNT, { bleed: 0.6, knock: -1 }, [
        ['{weapon} thuds against {victim}’s {part}.', '{weapon} glances off {victim}’s {part}.'],
        [
            '{weapon} strikes {victim}’s {part} and the toes sting.',
            '{weapon} bruises the top of {victim}’s {part}.',
        ],
        [
            '{weapon} strikes {victim}’s {part} and {victim} limps on it.',
            '{weapon} mashes {victim}’s toes against the ground.',
        ],
        [
            '{weapon} breaks the bones of {victim}’s {part}.',
            '{weapon} snaps {victim}’s ankle out of true.',
        ],
        [
            '{weapon} shatters {victim}’s {part}.',
            '{weapon} crushes {victim}’s {part} and the ankle turns under {them}.',
        ],
        [
            '{weapon} crushes {victim}’s {part} to pulp.',
            '{weapon} breaks every bone in {victim}’s {part}.',
        ],
    ]),
};
//# sourceMappingURL=blunt.js.map